import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * Tono del dialogo.
 * - `danger` (rojo): acciones destructivas de verdad (borrado fisico).
 * - `warning` (ambar): advertencias reversibles, como una desactivacion.
 * - `primary` (azul): el resto.
 */
export type ConfirmVariant = 'danger' | 'warning' | 'primary';

/**
 * Dialogo de confirmacion.
 *
 * Se abre con `NgbModal.open(ConfirmDialogComponent)`; sus campos son signals
 * publicos porque ng-bootstrap solo expone `componentInstance` (no hay
 * `setInput`). Lo normal es no usarlo directo, sino via `ConfirmService`.
 *
 * Resuelve `true` al confirmar y descarta la promesa al cancelar.
 */
@Component({
  selector: 'app-confirm-dialog',
  imports: [],
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  private readonly activeModal = inject(NgbActiveModal);

  readonly title = signal('Confirmar acción');
  readonly message = signal('Esta acción no se puede deshacer. ¿Deseas continuar?');

  /** Detalle opcional: el nombre del registro afectado, por ejemplo. */
  readonly detail = signal('');

  readonly confirmLabel = signal('Confirmar');
  readonly cancelLabel = signal('Cancelar');
  readonly variant = signal<ConfirmVariant>('danger');

  protected readonly isDanger = computed(() => this.variant() === 'danger');
  protected readonly isWarning = computed(() => this.variant() === 'warning');

  protected readonly icon = computed(() => {
    switch (this.variant()) {
      case 'danger':
        return 'exclamation-octagon-fill';
      case 'warning':
        return 'exclamation-triangle-fill';
      default:
        return 'question-circle-fill';
    }
  });

  protected confirm(): void {
    this.activeModal.close(true);
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
